import { supabase } from '../lib/supabase';
import { authServiceClient } from './authService';

interface ActivityData {
  activityType: 'before-after' | 'video';
  beforePhoto?: string; // base64
  afterPhoto?: string; // base64
  video?: File;
  location: string;
  description: string;
  coordinates?: { lat: number; lng: number };
}

export const activityService = {
  async submitActivity(data: ActivityData): Promise<{ success: boolean; points: number; error?: string }> {
    try {
      const user = await authServiceClient.getCurrentUser();
      if (!user) {
        return { success: false, points: 0, error: 'User not authenticated' };
      }

      let beforeImageUrl = null;
      let afterImageUrl = null;
      let videoUrl = null;
      let pointsEarned = 0;


      // Upload images to Supabase Storage
      if (data.beforePhoto && data.afterPhoto) {
        // Upload before photo
        const beforePhotoData = data.beforePhoto.replace(/^data:image\/\w+;base64,/, '');
        const beforePhotoBuffer = Uint8Array.from(atob(beforePhotoData), c => c.charCodeAt(0));
        const beforeFileName = `${user.id}/before_${Date.now()}.jpg`;
        
        const { data: beforeUpload, error: beforeError } = await supabase.storage
          .from('activity-photos')
          .upload(beforeFileName, beforePhotoBuffer, {
            contentType: 'image/jpeg',
            upsert: false
          });

        if (beforeError) throw beforeError;

        // Upload after photo
        const afterPhotoData = data.afterPhoto.replace(/^data:image\/\w+;base64,/, '');
        const afterPhotoBuffer = Uint8Array.from(atob(afterPhotoData), c => c.charCodeAt(0));
        const afterFileName = `${user.id}/after_${Date.now()}.jpg`;
        
        const { data: afterUpload, error: afterError } = await supabase.storage
          .from('activity-photos')
          .upload(afterFileName, afterPhotoBuffer, {
            contentType: 'image/jpeg',
            upsert: false
          });

        if (afterError) throw afterError;

        // Get public URLs
        const { data: { publicUrl: beforeUrl } } = supabase.storage
          .from('activity-photos')
          .getPublicUrl(beforeFileName);
        
        const { data: { publicUrl: afterUrl } } = supabase.storage
          .from('activity-photos')
          .getPublicUrl(afterFileName);

        beforeImageUrl = beforeUrl;
        afterImageUrl = afterUrl;
        pointsEarned = 10; // Base points for before/after
      }

      // Upload video if provided
      if (data.video) {
        const videoFileName = `${user.id}/video_${Date.now()}.mp4`;
        
        const { data: videoUpload, error: videoError } = await supabase.storage
          .from('activity-videos')
          .upload(videoFileName, data.video, {
            contentType: data.video.type,
            upsert: false
          });

        if (videoError) throw videoError;

        const { data: { publicUrl } } = supabase.storage
          .from('activity-videos')
          .getPublicUrl(videoFileName);

        videoUrl = publicUrl;
        pointsEarned = 15; // Base points for video
      }

      // Save activity to database with pending status
      const { data: activity, error: activityError } = await supabase
        .from('activities')
        .insert({
          profile_id: user.id,
          type: data.activityType,
          location: data.location,
          description: data.description,
          before_image: beforeImageUrl,
          after_image: afterImageUrl,
          video_url: videoUrl,
          points_earned: pointsEarned,
          status: 'pending' // New activities start as pending
        })
        .select()
        .single();

      if (activityError) throw activityError;

      // Don't update user stats immediately - wait for admin approval
      // Points will be awarded when admin approves the activity

      return { success: true, points: 0 }; // Return 0 points until approved
    } catch (error) {
      return { 
        success: false, 
        points: 0, 
        error: error instanceof Error ? error.message : 'Failed to submit activity' 
      };
    }
  },

  async updateUserStats(userId: string, points: number, activityType: 'before-after' | 'video') {
    try {
      // Get current user stats
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('points, total_activities, before_after_count, video_count, streak, last_activity_date')
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;

      const today = new Date().toISOString().split('T')[0];
      const lastActivityDate = profile.last_activity_date?.split('T')[0];
      
      // Calculate streak
      let newStreak = profile.streak || 0;
      if (lastActivityDate) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        
        if (lastActivityDate === yesterdayStr) {
          newStreak = profile.streak + 1;
        } else if (lastActivityDate !== today) {
          newStreak = 1;
        }
      } else {
        newStreak = 1;
      }

      // Update profile stats
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          points: profile.points + points,
          total_activities: profile.total_activities + 1,
          before_after_count: activityType === 'before-after' ? profile.before_after_count + 1 : profile.before_after_count,
          video_count: activityType === 'video' ? profile.video_count + 1 : profile.video_count,
          streak: newStreak,
          last_activity_date: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) throw updateError;
    } catch (error) {
      throw error;
    }
  },

  async createStorageBuckets() {
    try {
      // Create activity-photos bucket if it doesn't exist
      const { data: photoBucket, error: photoError } = await supabase.storage.createBucket('activity-photos', {
        public: true,
        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp']
      });

      if (photoError && !photoError.message.includes('already exists')) {
      }

      // Create activity-videos bucket if it doesn't exist
      const { data: videoBucket, error: videoError } = await supabase.storage.createBucket('activity-videos', {
        public: true,
        allowedMimeTypes: ['video/mp4', 'video/webm', 'video/quicktime'],
        maxFileSize: 104857600 // 100MB
      });

      if (videoError && !videoError.message.includes('already exists')) {
      }

      // Create photos bucket for new session system if it doesn't exist
      try {
        const { data: buckets } = await supabase.storage.listBuckets();
        const photoBucketExists = buckets?.some(bucket => bucket.name === 'photos');
        
        if (!photoBucketExists) {
          const { data: sessionPhotoBucket, error: sessionPhotoError } = await supabase.storage.createBucket('photos', {
            public: true,
            allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp']
          });

          if (sessionPhotoError) {
          }
        }
      } catch (error) {
      }

      // Create avatars bucket if it doesn't exist
      try {
        const { data: buckets } = await supabase.storage.listBuckets();
        const avatarBucketExists = buckets?.some(bucket => bucket.name === 'avatars');
        
        if (!avatarBucketExists) {
          const { data: avatarBucket, error: avatarError } = await supabase.storage.createBucket('avatars', {
            public: true,
            allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
            maxFileSize: 5242880 // 5MB
          });

          if (avatarError) {
          } else {
          }
        } else {
        }
      } catch (error) {
      }

      return true;
    } catch (error) {
      return false;
    }
  },

  async createCleaningActivity(
    userId: string,
    duration: number,
    beforePhotos: string[],
    afterPhotos: string[],
    caption: string,
    difficulty: 'easy' | 'medium' | 'hard',
    isPublic: boolean = true
  ): Promise<{ success: boolean; activityId?: string; error?: string }> {
    try {
      const photoUrls: { before: string[], after: string[] } = { before: [], after: [] };
      
      // Upload before photos
      for (let i = 0; i < beforePhotos.length; i++) {
        const photoData = beforePhotos[i].replace(/^data:image\/\w+;base64,/, '');
        const photoBuffer = Uint8Array.from(atob(photoData), c => c.charCodeAt(0));
        const fileName = `${userId}/before_${Date.now()}_${i}.jpg`;
        
        const { data: upload, error } = await supabase.storage
          .from('photos')
          .upload(fileName, photoBuffer, {
            contentType: 'image/jpeg',
            upsert: false
          });

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('photos')
          .getPublicUrl(fileName);
        
        photoUrls.before.push(publicUrl);
      }
      
      // Upload after photos
      for (let i = 0; i < afterPhotos.length; i++) {
        const photoData = afterPhotos[i].replace(/^data:image\/\w+;base64,/, '');
        const photoBuffer = Uint8Array.from(atob(photoData), c => c.charCodeAt(0));
        const fileName = `${userId}/after_${Date.now()}_${i}.jpg`;
        
        const { data: upload, error } = await supabase.storage
          .from('photos')
          .upload(fileName, photoBuffer, {
            contentType: 'image/jpeg',
            upsert: false
          });

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('photos')
          .getPublicUrl(fileName);
        
        photoUrls.after.push(publicUrl);
      }
      
      // Calculate points based on photo pairs and difficulty
      const basePairs = Math.min(beforePhotos.length, afterPhotos.length);
      const difficultyMultiplier = difficulty === 'easy' ? 0.8 : difficulty === 'hard' ? 1.5 : 1;
      const pointsEarned = Math.round(basePairs * 10 * difficultyMultiplier);
      
      // Create cleaning session with pending status
      const { data: session, error: sessionError } = await supabase
        .from('cleaning_sessions')
        .insert({
          user_id: userId,
          duration_minutes: duration,
          points_earned: pointsEarned,
          before_photos: photoUrls.before,
          after_photos: photoUrls.after,
          caption,
          difficulty,
          is_public: isPublic,
          status: 'pending', // New sessions start as pending
          completed_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (sessionError) throw sessionError;
      
      // Don't update user stats immediately - wait for admin approval
      // Points will be awarded when admin approves the activity
      
      return { success: true, activityId: session.id };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to create activity' 
      };
    }
  }
};